import axios, {
  type AxiosError,
  type InternalAxiosRequestConfig,
} from 'axios';
import type { TokenResponse } from '@/types/auth.types';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';
const AUTH_STORAGE_KEY = 'epiq-auth';

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

interface PersistedAuth {
  state?: {
    accessToken?: string | null;
    refreshToken?: string | null;
    [key: string]: unknown;
  };
  version?: number;
}

function readPersistedAuth(): PersistedAuth | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PersistedAuth) : null;
  } catch {
    return null;
  }
}

function persistRefreshedTokens(tokens: TokenResponse): void {
  const persisted = readPersistedAuth();
  if (!persisted?.state) return;
  persisted.state.accessToken = tokens.access_token;
  persisted.state.refreshToken = tokens.refresh_token;
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(persisted));
  window.dispatchEvent(
    new CustomEvent<TokenResponse>('klara:auth-refreshed', { detail: tokens })
  );
}

function expireSession(): void {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  window.dispatchEvent(new Event('klara:auth-expired'));
}

export const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = readPersistedAuth()?.state?.accessToken;
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

let refreshRequest: Promise<TokenResponse> | null = null;

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const request = error.config as RetryableRequestConfig | undefined;
    const requestUrl = request?.url ?? '';
    const isAuthRequest = /\/auth\/(login|refresh)$/.test(requestUrl);

    if (error.response?.status !== 401 || !request || request._retry || isAuthRequest) {
      return Promise.reject(error);
    }

    const refreshToken = readPersistedAuth()?.state?.refreshToken;
    if (!refreshToken) {
      expireSession();
      return Promise.reject(error);
    }

    request._retry = true;
    try {
      refreshRequest ??= axios
        .post<TokenResponse>(`${BASE_URL}/auth/refresh`, { refresh_token: refreshToken })
        .then((response) => response.data)
        .finally(() => {
          refreshRequest = null;
        });
      const tokens = await refreshRequest;
      persistRefreshedTokens(tokens);
      request.headers.Authorization = `Bearer ${tokens.access_token}`;
      return apiClient(request);
    } catch (refreshError) {
      expireSession();
      return Promise.reject(refreshError);
    }
  }
);
